import React, {useState, useRef, useMemo, useCallback} from 'react';
import PropTypes from 'prop-types';
import {Modal} from '@/components/atoms';
import {Button, Icon} from '@pepsico-ds/ui';
import {UploadFile} from '@/components/molecules';
import {uploadFileDescriptionForCSV} from '@/utils/commonTemplate';
import {Constants} from '@/utils/constants/commonConstants';
import {MODAL_TYPE} from '@/utils/constants/globalStyle';
import {useAppDispatch} from '@/store';
import {setStoreListUrl} from '@/store/slices/salesTargetSlice';
import NotificationAlert from '@/components/atoms/Toast/NotificationAlert';
import UploadValidationErrors from './UploadValidationErrors';
import './fileUploadWithModal.scss';

export const UploadResult = Object.freeze({
  NONE: 'none',
  SUCCESS: 'success',
  ERROR: 'error'
});

const FileUploadWithModal = ({
  uploadFor = null,
  openUpload,
  setOpenUpload,
  formData,
  setFormData,
  title = 'Upload Targets',
  showTertiaryButton = false,
  fileType,
  isCsvValidationRequired = false,
  customRules = null,
  onClear,
  onCloseModal,
  customErrorMessages = {},
  onTemplateUpdate,
  salesTargetId,
  customUploadFile = null,
  onDelete = () => {},
  onClickPrimary = null,
  primaryButtonDisabled = false,
  uploading = false,
  setUploading = () => {},
  uploadProgress = 0,
  required,
  primaryButtonText = 'Done',
  emptyPrimaryButtonText = primaryButtonText,
  showDownloadTemplate = false,
  handleDownloadTemplate = () => {},
  subTitle = '',
  uploadErrorMessage = '',
  handleCloseNotification = () => {},
  handleCloseModal = () => {},
  displayOkButton = false,
  onSecondaryOk = () => {},
  ...props
}) => {
  const [uploadError, setUploadError] = useState(false);
  const [secondaryBtnText, setSecondaryBtnText] = useState(Constants.Cancel);
  const dispatch = useAppDispatch();
  const fileUploadResultRef = useRef(UploadResult.NONE);

  const hasSelectedFile = useMemo(() => {
    const mediaFile = formData?.targetMediaFile;

    return (
      Array.isArray(mediaFile) &&
      mediaFile.length > 0 &&
      typeof mediaFile[0] === 'string' &&
      mediaFile[0].trim() !== ''
    );
  }, [formData?.targetMediaFile]);

  const handleDelete = useCallback(() => {
    if (hasSelectedFile) return;

    if (setFormData) {
      setFormData((prev) => ({...prev, targetMediaFile: []}));
    }

    onDelete();
  }, [hasSelectedFile, setFormData, onDelete]);

  const isCloseModal = useCallback(() => {
    setOpenUpload(false);
    handleDelete();
    setSecondaryBtnText(Constants.Cancel);
    handleCloseModal();
    if (onClear) onClear();
    if (onCloseModal) onCloseModal(fileUploadResultRef.current);
  }, [setOpenUpload, handleDelete, handleCloseModal, onClear, onCloseModal]);

  const files = useMemo(() => {
    if (hasSelectedFile) return formData?.targetMediaFile;
    return null;
  }, [hasSelectedFile, formData?.targetMediaFile]);

  const isFileError = useMemo(
    () => secondaryBtnText !== Constants.Cancel,
    [secondaryBtnText]
  );

  const isUploadInProgress = useMemo(
    () =>
      uploadFor == Constants.BulkRewards &&
      uploading &&
      uploadProgress < 100 &&
      uploadProgress > 0,
    [uploadFor, uploading, uploadProgress]
  );

  const handleModalClose = useCallback(() => {
    handleDelete();
    isCloseModal();
    setUploadError(false);
  }, [handleDelete, isCloseModal]);

  const handleSecondaryButtonClick = useCallback(() => {
    if (displayOkButton && secondaryBtnText === Constants.Ok) {
      onSecondaryOk();
    }
    handleDelete();
    isCloseModal();
    setUploadError(false);
  }, [
    displayOkButton,
    secondaryBtnText,
    onSecondaryOk,
    handleDelete,
    isCloseModal
  ]);

  const handlePrimaryButtonClick = useCallback(() => {
    if (typeof onClickPrimary === 'function') {
      onClickPrimary();
    } else {
      if (!hasSelectedFile) {
        setUploadError(true);
        return;
      } else {
        onTemplateUpdate?.(
          salesTargetId,
          formData?.storeList || formData?.targetMediaFile?.[0]
        );
      }

      isCloseModal();
    }
  }, [
    onClickPrimary,
    hasSelectedFile,
    formData?.targetMediaFile,
    formData?.storeList,
    onTemplateUpdate,
    salesTargetId,
    isCloseModal,
    setUploadError
  ]);

  const handleUpload = useCallback(
    (file) => {
      setFormData((prev) => ({...prev, targetMediaFile: [file?.name]}));
      fileUploadResultRef.current = UploadResult.SUCCESS;
      setUploadError(false);
    },
    [setFormData, setUploadError]
  );

  const handleUploadDelete = useCallback(() => {
    setFormData((prev) => ({...prev, targetMediaFile: [], storeList: ''}));
    dispatch(setStoreListUrl(''));
    onDelete();
  }, [setFormData, dispatch, onDelete]);

  const handleUploadError = useCallback(
    (error) => {
      if (error) {
        fileUploadResultRef.current = UploadResult.ERROR;
        setUploadError(true);
      }
    },
    [setUploadError]
  );

  return (
    <Modal
      isDark={true}
      onCloseModal={handleModalClose}
      onClickSecondaryButton={handleSecondaryButtonClick}
      onClickPrimaryButton={handlePrimaryButtonClick}
      showModal={openUpload}
      className='upload-target-modal'
      title={title}
      primaryButtonText={
        hasSelectedFile ? primaryButtonText : emptyPrimaryButtonText
      }
      hideCloseButton={false}
      primaryButtonDisabled={
        primaryButtonDisabled || isFileError || !hasSelectedFile
      }
      type={MODAL_TYPE.SMALL}
      showSecondaryButton={true}
      secondaryButtonText={secondaryBtnText}
      data-testId='upload-target-modal'
      showTertiaryButton={isFileError && showTertiaryButton}
      onClickTertiaryButton={isCloseModal}
      tertiaryButtonText='Cancel'
      secondaryButtonDisabled={uploading}
      content={
        <>
          <NotificationAlert
            type='common'
            message={{subTitle: Constants.UploadInProgressMessage}}
            show={isUploadInProgress}
            iconType={'error_outline'}
            handleClick={handleCloseNotification}
          />
          <UploadFile
            uploadFor={uploadFor}
            acceptedExtensions={Constants.CSV}
            description={uploadFileDescriptionForCSV()}
            uploadIcon='cloud_upload'
            label={subTitle || Constants.UploadStoresFile}
            variant={uploading ? Constants.Uploading : Constants.Edit}
            feedbackType={
              formData?.feedbackType ||
              (uploadError ? Constants.Error : Constants.Info)
            }
            required={required}
            files={files}
            getFileUrl={(url) =>
              setFormData((prev) => ({...prev, storeList: url}))
            }
            onUpload={handleUpload}
            onUploadDelete={handleUploadDelete}
            onUploadError={handleUploadError}
            helperText={
              formData?.helperText ||
              (uploadError ? 'Wrong file type, please use CSV file' : '')
            }
            customErrorMessages={customErrorMessages}
            type={Constants.Single}
            showDownloadTemplate={false}
            showHandleViewTable={false}
            className={
              formData?.targetMediaFile?.length > 0 && isFileError
                ? 'disabled'
                : ''
            }
            containerClassName={
              formData?.targetMediaFile?.length > 0 ? 'disabled' : ''
            }
            dataTestId='upload-file'
            setSecondaryBtnText={setSecondaryBtnText}
            displayOkButton={displayOkButton}
            uploading={uploading}
            setUploading={setUploading}
            fileType={fileType}
            isCsvValidationRequired={isCsvValidationRequired}
            customRules={customRules}
            customUploadFile={customUploadFile}
            progressPercentage={uploadProgress}
            uploadErrorMessage={uploadErrorMessage}
            {...props}
          />
          {formData?.feedbackType === 'error' && formData?.helperText && (
            <UploadValidationErrors helperText={formData.helperText} />
          )}
          {showDownloadTemplate && !uploadErrorMessage && (
            <Button
              onClick={() => {
                handleDownloadTemplate(uploadFor);
              }}
              className='download-template-button'
              variant='tertiary'
              size='small'
              iconPosition='start'
              disabled={uploading || isFileError || uploadError}>
              <Icon icon='save_alt' size='small' style={{marginRight: '4px'}} />{' '}
              Download Template
            </Button>
          )}
        </>
      }
    />
  );
};

FileUploadWithModal.propTypes = {
  uploadFor: PropTypes.string,
  openUpload: PropTypes.bool.isRequired,
  setOpenUpload: PropTypes.func.isRequired,
  formData: PropTypes.shape({
    targetMediaFile: PropTypes.array,
    storeList: PropTypes.string
  }),
  setFormData: PropTypes.func.isRequired,
  title: PropTypes.string,
  showTertiaryButton: PropTypes.bool,
  fileType: PropTypes.string,
  isCsvValidationRequired: PropTypes.bool,
  customRules: PropTypes.object,
  onClear: PropTypes.func,
  onCloseModal: PropTypes.func,
  customErrorMessages: PropTypes.object,
  onTemplateUpdate: PropTypes.func,
  salesTargetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  customUploadFile: PropTypes.string,
  onDelete: PropTypes.func,
  onClickPrimary: PropTypes.func,
  primaryButtonDisabled: PropTypes.bool,
  uploading: PropTypes.bool,
  setUploading: PropTypes.func,
  uploadProgress: PropTypes.number,
  required: PropTypes.bool,
  primaryButtonText: PropTypes.string,
  emptyPrimaryButtonText: PropTypes.string,
  showDownloadTemplate: PropTypes.bool,
  handleDownloadTemplate: PropTypes.func,
  subTitle: PropTypes.string,
  uploadErrorMessage: PropTypes.string,
  handleCloseNotification: PropTypes.func,
  handleCloseModal: PropTypes.func,
  displayOkButton: PropTypes.bool,
  onSecondaryOk: PropTypes.func
};

export default React.memo(FileUploadWithModal);
